<script lang="ts">
  import { onDestroy } from 'svelte';
  import type { Snippet } from 'svelte';
  import { api } from '../../api/client';
  import type { TimelineImport } from '../../api/types';
  import { confirm } from '../../stores/confirmDialog';
  import { when } from '../../utils/time';
  import { integer, t } from '../../i18n/index.ts';
  import { IMPORT_ACCEPT, IMPORT_FORMATS, importProgress, importSummary, type ImportPart } from '../../utils/imports';

  // ImportList uploads one file to import and lists one part of the member's
  // imports with what each added to it and skipped (005 FR-069, 006 FR-120):
  // the timeline's Imports tab the ones that read location history, and
  // Settings, Activities the ones that read Google Fit data (005 FR-076,
  // 006 FR-126). A Google Takeout archive is one import for the timeline,
  // activities, and health history (005 FR-073), so one with both is in both
  // lists and deleting it from either removes all of it.
  let { title, part, children }: { title: string; part: ImportPart; children: Snippet } = $props();

  let imports = $state<TimelineImport[]>([]);
  let progress = $state<number | null>(null);
  let error = $state('');
  let input: HTMLInputElement | undefined = $state();
  let poll: ReturnType<typeof setTimeout> | null = null;

  $effect(() => {
    load();
  });
  onDestroy(() => poll && clearTimeout(poll));

  // load lists imports, checking again while one is still being read.
  async function load() {
    try {
      imports = await api.get<TimelineImport[]>(`/me/timeline/imports?part=${part}`);
    } catch (e: any) {
      error = e.message;
    }
    if (poll) clearTimeout(poll);
    if (imports.some((i) => i.status === 'processing')) poll = setTimeout(load, 2000);
  }

  async function send(file: File) {
    error = '';
    progress = 0;
    const form = new FormData();
    form.append('file', file);
    try {
      await api.upload('/me/timeline/imports', form, (sent, total) => (progress = total ? sent / total : 0));
    } catch (e: any) {
      error = e.code === 'ALREADY_IMPORTED' ? t('componentsImportDuplicate', { file: file.name }) : e.message;
    } finally {
      progress = null;
      if (input) input.value = '';
      load();
    }
  }

  async function remove(i: TimelineImport) {
    if (!(await confirm(t('componentsImportDeleteConfirm', { file: i.file_name })))) return;
    await api.delete(`/me/timeline/imports/${i.id}`).catch((e: any) => (error = e.message));
    load();
  }

  const days = (i: TimelineImport) =>
    i.first_day ? (i.first_day === i.last_day ? i.first_day : t('componentsImportDays', { first: i.first_day, last: i.last_day })) : '';

  // line is what an import is, when it was uploaded, and the days it covers.
  function line(i: TimelineImport): string {
    const format = IMPORT_FORMATS[i.format] ?? t('componentsImportDetecting');
    const d = days(i);
    if (d) return t('componentsImportLineDays', { format, when: when(i.created_at), days: d });
    return t('componentsImportLine', { format, when: when(i.created_at) });
  }
</script>

<section class="card p-5 space-y-3">
  <h2 class="font-semibold">{title}</h2>
  <div class="text-sm text-text-muted space-y-2">{@render children()}</div>
  <input bind:this={input} type="file" class="hidden" accept={IMPORT_ACCEPT}
    onchange={(e) => { const f = e.currentTarget.files?.[0]; if (f) send(f); }} />
  {#if progress !== null}
    <div class="h-2 rounded" style="background: var(--theme-surface0);" role="progressbar" aria-label={t('componentsImportUploadLabel')} aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(progress * 100)}>
      <div class="h-2 rounded" style="width: {Math.round(progress * 100)}%; background: var(--theme-accent);"></div>
    </div>
    <p class="text-sm text-text-muted">{t('componentsImportUploading', { percent: integer(Math.round(progress * 100)) })}</p>
  {:else}
    <button class="btn btn-primary btn-sm" onclick={() => input?.click()}><i aria-hidden="true" class="las la-file-upload mr-1"></i>{t('componentsImportChoose')}</button>
  {/if}
  {#if error}<p class="text-sm" style="color: var(--theme-error);" role="alert">{error}</p>{/if}
</section>

{#each imports as i (i.id)}
  <section class="card p-5 flex gap-4 items-start">
    <i aria-hidden="true" class="las {i.status === 'failed' ? 'la-exclamation-triangle' : i.status === 'processing' ? 'la-spinner' : 'la-file-import'} text-2xl"></i>
    <div class="flex-1 min-w-0 space-y-1">
      <p class="font-medium truncate">{i.file_name}</p>
      <p class="text-xs text-text-muted">{line(i)}</p>
      {#if i.status === 'processing'}
        <p class="text-sm" aria-live="polite">{importProgress(i, part)}</p>
      {:else if i.status === 'failed'}
        <p class="text-sm" style="color: var(--theme-error);">{i.error}</p>
      {:else}
        {#each importSummary(i, part) as line}
          <p class="text-sm">{line}</p>
        {/each}
      {/if}
    </div>
    {#if i.status !== 'processing'}
      <button class="btn btn-secondary btn-sm" onclick={() => remove(i)}><i aria-hidden="true" class="las la-trash mr-1"></i>{t('appDelete')}</button>
    {/if}
  </section>
{/each}
